import React, { useEffect, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { X } from 'lucide-react'
import Button from '../ui/Button'
import { closeLoadingModal, showErrorModal, showLoadingModal, showSuccessModal } from '../ui/ConfirmDialog'
import { createProjectBranch, type ProjectBranch } from '../../services/projectService'
import { getApiMessage } from '../../utils/apiMessage'

interface CreateProjectDialogProps {
  open: boolean
  codeIndexId?: string
  currentUserName?: string | null
  onClose: () => void
  onCreated: (project: ProjectBranch) => void
}

function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function CreateProjectDialog({
  open,
  codeIndexId,
  currentUserName,
  onClose,
  onCreated,
}: CreateProjectDialogProps) {
  const [name, setName] = useState('')
  const [targetRevision, setTargetRevision] = useState('')
  const [changeEcoNumber, setChangeEcoNumber] = useState('')
  const [description, setDescription] = useState('')
  const [createdDate, setCreatedDate] = useState(formatDisplayDate(new Date()))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setTargetRevision('')
    setChangeEcoNumber('')
    setDescription('')
    setCreatedDate(formatDisplayDate(new Date()))
    setSaving(false)
  }, [open])

  const handleCreate = async () => {
    const trimmedName = name.trim()
    const trimmedTargetRevision = targetRevision.trim()
    const trimmedChangeEcoNumber = changeEcoNumber.trim()
    if (!codeIndexId || !trimmedName || !trimmedTargetRevision || !trimmedChangeEcoNumber || saving) return

    setSaving(true)
    showLoadingModal({
      title: 'Creating Project',
      message: 'Please wait while the project is being created...',
    })

    try {
      const project = await createProjectBranch(codeIndexId, {
        name: trimmedName,
        targetRevision: trimmedTargetRevision,
        changeEcoNumber: trimmedChangeEcoNumber,
        description: description.trim() || null,
      })
      closeLoadingModal()
      await showSuccessModal({
        title: 'Project Created',
        description: getApiMessage(project) || 'Project created successfully.',
      })
      onCreated(project)
    } catch (err: any) {
      console.error('Failed to create project.', err)
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to create project',
        description: getApiMessage(err) || 'Action failed. Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 430, maxWidth: 'calc(100vw - 32px)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 1 }}>
        <span className="text-lg font-semibold text-gray-900">Create Project</span>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <X className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-gray-700">
            Project Number
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={saving}
              required
              autoFocus
              className="mt-1 h-8 w-full rounded border border-gray-300 px-2 text-sm text-gray-900 outline-none focus:border-[#1D5176] focus:ring-1 focus:ring-[#1D5176] disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Revision Number
            <input
              value={targetRevision}
              onChange={(event) => setTargetRevision(event.target.value)}
              disabled={saving}
              required
              className="mt-1 h-8 w-full rounded border border-gray-300 px-2 text-sm text-gray-900 outline-none focus:border-[#1D5176] focus:ring-1 focus:ring-[#1D5176] disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Change/ECO Number
            <input
              value={changeEcoNumber}
              onChange={(event) => setChangeEcoNumber(event.target.value)}
              disabled={saving}
              required
              className="mt-1 h-8 w-full rounded border border-gray-300 px-2 text-sm text-gray-900 outline-none focus:border-[#1D5176] focus:ring-1 focus:ring-[#1D5176] disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Description
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={saving}
              rows={3}
              className="mt-1 w-full resize-none rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-[#1D5176] focus:ring-1 focus:ring-[#1D5176] disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Created by
            <input
              value={currentUserName || '--'}
              disabled
              className="mt-1 h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-sm text-gray-500"
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Date
            <input
              value={createdDate}
              disabled
              className="mt-1 h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-sm text-gray-500"
            />
          </label>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => void handleCreate()} disabled={!name.trim() || !targetRevision.trim() || !changeEcoNumber.trim() || saving}>
          {saving ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
